import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CachedIcon from '@mui/icons-material/Cached';
import axios from 'axios';
import { Formik, getIn } from 'formik';
import * as Yup from 'yup';

import WithConfirm from '~/components/ConfirmModal';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import CancelButton from '~/components/core/Buttons/CancelButton';
import CountryMultiSelectFormik from '~/components/core/Formik/CountriesMultiselectFormik/CountryMultiSelectFormik';
import SearchWithOptionsFormik from '~/components/core/Formik/SearchWithOptionsFormik/SearchWithOptionsFormik';
import StateMultiselectFormik from '~/components/core/Formik/StatesMultiselectFormik/StateMultiSelectFormik';
import { CONFIGURATION_FEATURES_NAMES, COUNTRY_TO_STATE_MAP } from '~/Types';
import {
  containsOneCountryWithStates,
  getOrganizationCountryCode,
  isFeatureEnabled,
  isOrganizationWithStates,
  reportAxiosError,
} from '~/Utils';
import { downloadResponseAsFile } from '~/Utils/fileDownload';

import { serverDateTimeToLocal } from '../../../DateTimeUtils';
import AutocompleteFormik from '../../AutocompleteFormik';
import CardDialog from '../../CardDialog';
import { Text } from '../../core';
import { getAllowedDocumentTypesForLobs } from '../../Documents/utils/documentTypes';
import FileDropZone from '../../FileDropZone';
import useOrganization from '../../OrganizationContext';
import TextFieldFormik from '../../TextFieldFormik';
import { CoveragesMultiselectFormik } from '../../TPA/Coverages/CoveragesMultiselectFormik';
import { LobCheckboxMultiselectFormik } from '../../TPA/LOB/LobCheckboxMultiselectFormik';
import SubOrganizationMultiselectFormik from '../../TPA/SubOrganizations/SubOrganizationMultiselectFormik';
import { useSysconfig } from '../SystemConfigurationScreen';

import { useStyles } from '../../../assets/styles';
import styles from './OrganizationDocumentsTemplates.module.scss';

const ALL_OPTION = 'All';
const statesWithoutEmpty = Object.keys(COUNTRY_TO_STATE_MAP['US']).filter((state) => !!state);

// The template's own lines of business narrow which document types it may carry. `is_all_lobs`
// expands to every lob the organization supports, matching how the coverages field below reads
// the same flag — a type restricted to an unsupported lob is no more assignable than any other.
const getDocumentTypeOptions = (documentTypesDict, values, supportedClaimTypes) =>
  getAllowedDocumentTypesForLobs(documentTypesDict, (values.is_all_lobs ? supportedClaimTypes : values.lobs) || []);

// A template configured before its type was disabled, restricted to other lines of business or
// made non-public keeps that type: the option list offers it and the Yup rule exempts it, so the
// rest of the template stays editable. Anything else the form is holding has to be one of the
// currently available types.
const getDocumentTypeOptionsWithCurrent = (documentTypesDict, values, supportedClaimTypes, currentDocumentType) => {
  const availableDocumentTypes = getDocumentTypeOptions(documentTypesDict, values, supportedClaimTypes);

  return currentDocumentType && !availableDocumentTypes.includes(currentDocumentType)
    ? [...availableDocumentTypes, currentDocumentType]
    : availableDocumentTypes;
};

const getDocumentTemplateValidationScheme = (
  documentTypesDict,
  forbiddenNames,
  subOrganizationEnabled,
  multiCountryOrganization,
  shouldDisplayStates,
  supportedClaimTypes,
  currentDocumentType
) => {
  return Yup.object().shape({
    is_file_required: Yup.bool(),
    file_size: Yup.number().when('is_file_required', {
      is: true,
      then: Yup.number().required('Required').min(1, 'Required'),
    }),
    filename: Yup.string().required('Required').notOneOf(forbiddenNames, 'Filename already exists'),
    display_name: Yup.string().required('Required'),
    is_all_coverages: Yup.boolean(),
    coverages: Yup.array()
      .of(Yup.string())
      .when('is_all_coverages', {
        is: false,
        then: Yup.array().of(Yup.string()).min(1, 'At least one coverage required'),
      }),
    countries: Yup.array()
      .of(Yup.string())
      .when('is_all_countries', {
        is: (is_all_countries) => multiCountryOrganization && !is_all_countries,
        then: Yup.array().of(Yup.string()).min(1, 'Required'),
      }),
    is_all_countries: Yup.bool(),
    states: Yup.array().test('states-required', 'At least 1 state is required', (value, context) => {
      if (!shouldDisplayStates) return true;
      const is_all_states = context.parent['is_all_states'];
      return multiCountryOrganization || is_all_states || (value !== undefined && value.length > 0);
    }),
    comment: Yup.string().required('Required'),
    document_type: Yup.string()
      .required('Required')
      .test('is-available-document-type', 'Document type is not available for this template', function (value) {
        return (
          !value ||
          getDocumentTypeOptionsWithCurrent(
            documentTypesDict,
            this.parent,
            supportedClaimTypes,
            currentDocumentType
          ).includes(value)
        );
      }),
    is_all_sub_orgs: Yup.bool(),
    sub_organization_ids: subOrganizationEnabled
      ? Yup.array().when('is_all_sub_orgs', {
          is: true,
          then: Yup.array().max(0, 'Cannot select sub-organizations when All is also selected'),
          otherwise: Yup.array().min(1, 'At least one sub-organization required'),
        })
      : Yup.array().max(0, 'Sub-organizations must be empty'),
    is_all_lobs: Yup.bool(),
    lobs: Yup.array().when('is_all_lobs', {
      is: true,
      then: Yup.array().max(0, 'Cannot select lines of business when All is also selected'),
      otherwise: Yup.array().min(1, 'At least one line of business is required'),
    }),
  });
};

function DocumentTemplateFormInner(props) {
  const {
    uploadPercent,
    values,
    errors,
    touched,
    setFieldTouched,
    setFieldValue,
    isSubmitting,
    handleSubmit,
    onCancel,
    uploadDocument,
    documentTemplate,
  } = props;
  const [showUploadFile, setShowUploadFile] = useState(false);
  const { classes } = useStyles();
  const { organization } = useSysconfig();
  const { subOrganizations, supportedClaimTypes, documentTypesDict, subOrganizationEnabled } = useOrganization();

  // Keyed on the saved type, exactly like the Yup rule's exemption — seeding it from
  // `values.document_type` instead would keep offering a live pick the lobs have since
  // narrowed away, which the rule would then reject with no way to clear it.
  const documentTypeOptions = getDocumentTypeOptionsWithCurrent(
    documentTypesDict,
    values,
    supportedClaimTypes,
    documentTemplate?.document_type
  );
  const isSelectedDocumentTypeOffered = !values.document_type || documentTypeOptions.includes(values.document_type);

  // Narrowing the lobs can strand the selected type; drop it rather than leave the form
  // holding a value the picker no longer offers and the validation will not accept.
  useEffect(() => {
    if (!isSelectedDocumentTypeOffered) {
      setFieldValue('document_type', '');
    }
  }, [isSelectedDocumentTypeOffered, setFieldValue]);

  const multiCountryOrganization = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.MULTI_COUNTRY_ORGANIZATION
  );

  const shouldDisplayStates = isOrganizationWithStates(organization) || multiCountryOrganization;

  const onFileSelect = (file) => {
    setFieldValue('file', file);
    setFieldTouched('file', true);
    setFieldValue('file_size', file.size);
    setFieldTouched('file_size', true);
    setFieldValue('filename', file.name);
    setFieldTouched('filename', true);
    setFieldValue('display_name', file.name.split('.')[0]);
    setFieldTouched('display_name', true);
  };

  const onCancelFileSelect = () => {
    setFieldValue('file', undefined);
    setFieldValue('file_size', undefined);
    setFieldValue('filename', '');
    setFieldValue('display_name', '');
  };

  const onShowUploadFile = () => {
    setShowUploadFile(true);
    setFieldValue('is_file_required', true);
  };

  if (!showUploadFile && uploadDocument) {
    onShowUploadFile();
  }

  return (
    <Grid container>
      {!showUploadFile && (
        <Grid item xs={12}>
          <div className={classes.containerCentered}>
            <CachedIcon color="primary" className={classes.leftButtonIcon} />
            <Button variant="outlined" color="primary" disabled={isSubmitting} onClick={onShowUploadFile}>
              Update document template content
            </Button>
          </div>
        </Grid>
      )}

      {showUploadFile && (
        <Grid item xs={12}>
          <FileDropZone
            onFileSelect={onFileSelect}
            onCancelFileSelect={onCancelFileSelect}
            file={values['file']}
            uploadPercent={uploadPercent}
            error={getIn(errors, 'file_size') && getIn(touched, 'file_size')}
            errorMessage={getIn(errors, 'file_size')}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <TextFieldFormik id="display_name" label="Display Name" className={classes.textField} fullWidth />
      </Grid>
      {subOrganizationEnabled && (
        <Grid item xs={12}>
          <SubOrganizationMultiselectFormik
            subOrganizationsFieldId="sub_organization_ids"
            allSelectedFieldId="is_all_sub_orgs"
            selectAllLabel="All (inc. future sub-orgs)"
            showAllInRenderedSelectedOptions
            shouldDisplayAllOption
          />
        </Grid>
      )}
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <LobCheckboxMultiselectFormik
          lobsFieldId="lobs"
          allSelectedFieldId="is_all_lobs"
          subOrganizationIds={values.is_all_sub_orgs ? subOrganizations.map((s) => s.id) : values.sub_organization_ids}
          shouldIncludeLabel
        />
      </Grid>
      {multiCountryOrganization ? (
        <Grid item xs={12}>
          <CountryMultiSelectFormik
            id="countries"
            disabled={isSubmitting}
            statesFieldIdToResetOnChange="states"
            allSelectedFieldId="is_all_countries"
            limitTags={3}
          />
        </Grid>
      ) : null}
      {shouldDisplayStates ? (
        <Grid item xs={12}>
          <StateMultiselectFormik
            statesFieldId="states"
            allSelectedFieldId="is_all_states"
            disabled={
              isSubmitting || !(multiCountryOrganization ? containsOneCountryWithStates(values.countries) : true)
            }
            showAllInRenderedSelectedOptions
            shouldDisplayAllOption
            country={
              multiCountryOrganization && containsOneCountryWithStates(values.countries)
                ? values.countries[0]
                : getOrganizationCountryCode(organization)
            }
            isAllOptionChecksAllCheckboxes
          />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <CoveragesMultiselectFormik
          coveragesFieldId="coverages"
          subOrganizationIds={values.is_all_sub_orgs ? subOrganizations.map((s) => s.id) : values.sub_organization_ids}
          lobs={values.is_all_lobs ? supportedClaimTypes : values.lobs}
          allCoveragesFieldId="is_all_coverages"
          selectAllLabel="All"
          shouldDisplayAll
          showAllInRenderedSelectedOptions
          isAllOptionChecksAllCheckboxes
        />
      </Grid>
      <Grid item xs={12}>
        <TextFieldFormik id="filename" label="File Name" className={classes.textField} fullWidth />
      </Grid>
      <Grid item xs={12}>
        <TextFieldFormik id="comment" label="Comment" className={classes.textField} fullWidth />
      </Grid>
      <Grid item xs={12}>
        <AutocompleteFormik
          id="document_type"
          label="Document Type"
          options={documentTypeOptions}
          getOptionLabel={(option) => documentTypesDict?.[option]?.['desc'] || option}
          sortAlphabetic
        />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.buttonsContainer}>
          <CancelButton disabled={isSubmitting} onClick={onCancel} />
          {documentTemplate?.is_attached_to_email_template ? (
            <WithConfirm
              title="Edit Document Template"
              contentText="This document template is currently attached to an email template. Are you sure you want to edit it?"
              primaryButtonName="Yes, Edit"
              secondaryButtonName="Cancel"
              shouldCloseOnPrimary={true}
            >
              <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
                Save
              </Button>
            </WithConfirm>
          ) : (
            <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
              Save
            </Button>
          )}
        </div>
      </Grid>
    </Grid>
  );
}

DocumentTemplateFormInner.propTypes = {
  touched: PropTypes.object.isRequired, // From Formik
  errors: PropTypes.object.isRequired, // From Formik
  values: PropTypes.object.isRequired, // From Formik
  setFieldValue: PropTypes.func.isRequired, // From Formik
  isSubmitting: PropTypes.bool.isRequired, // From Formik
  handleSubmit: PropTypes.func.isRequired, // From Formik
  resetForm: PropTypes.func.isRequired, // From Formik
  onCancel: PropTypes.func.isRequired, // From Formik
  setFieldTouched: PropTypes.func.isRequired, // From Formik
  uploadPercent: PropTypes.number.isRequired,
  uploadDocument: PropTypes.bool,
  documentTemplate: PropTypes.object,
};

const getDocumentUploadConfig = (setUploadPercent) => {
  const config = {
    onUploadProgress: (progressEvent) => {
      var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      setUploadPercent(percentCompleted);
    },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  return config;
};

const getDocumentUploadFormData = (values) => {
  const formData = new FormData();
  if (values.is_file_required) {
    formData.append('file', values['file']);
  }
  formData.append('filename', values['filename']);
  formData.append('display_name', values['display_name']);
  formData.append('is_all_sub_orgs', values['is_all_sub_orgs']);
  formData.append('sub_organization_ids', values['sub_organization_ids']);
  formData.append('is_all_lobs', values['is_all_lobs']);
  formData.append('lobs', values['lobs']);
  formData.append('coverages', values['coverages']);

  formData.append('is_all_coverages', values['is_all_coverages']);
  formData.append('countries', values['countries']);
  formData.append('is_all_countries', values['is_all_countries']);
  formData.append(
    'states',
    values['states'].length === 1 && values['states'][0] === 'All' ? statesWithoutEmpty : values['states']
  );
  formData.append('comment', values['comment']);
  formData.append('document_type', values['document_type']);
  return formData;
};

export function UpdateDocumentTemplateFormik(props) {
  const { documentTemplate, orgId, onDocumentUpdate, documentTemplates, isOpen, onClose } = props;
  const [uploadPercent, setUploadPercent] = useState(0);
  const { organization } = useSysconfig();
  const multiCountryOrganization = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.MULTI_COUNTRY_ORGANIZATION
  );
  const {
    organizationOperationalDetails: { documentTypesDict, subOrganizationEnabled },
  } = useSysconfig();
  const { supportedClaimTypes } = useOrganization();

  const shouldDisplayStates = isOrganizationWithStates(organization) || multiCountryOrganization;

  const handleDocumentSubmit = async (values, formikProps) => {
    const { setSubmitting, resetForm } = formikProps;

    const formData = getDocumentUploadFormData(values);
    const config = getDocumentUploadConfig(setUploadPercent);

    try {
      await axios.patch(
        `/sysconfig/api/v1/document_templates/organizations/${orgId}/update_document_template/${documentTemplate.id}`,
        formData,
        config
      );
      await onDocumentUpdate();
      resetForm();
      onClose(false);
    } catch (error) {
      reportAxiosError(error);
    }
    setUploadPercent(0);
    setSubmitting(false);
  };

  const handleCancel = (resetForm) => {
    onClose(false);
    resetForm();
  };
  const forbiddenNames = documentTemplates
    .map((doc) => doc.filename)
    .filter((filename) => filename !== documentTemplate.filename);

  return (
    <Formik
      initialValues={{
        ...documentTemplate,
        sub_organization_ids: documentTemplate.sub_organization_ids || [],
        file_size: '',
        is_file_required: false,
        comment: documentTemplate?.comment || '',
        countries: multiCountryOrganization
          ? documentTemplate?.countries || []
          : [getOrganizationCountryCode(organization)],
        is_all_countries: documentTemplate?.is_all_countries,
        states:
          documentTemplate.states && documentTemplate.states.length === statesWithoutEmpty.length
            ? [ALL_OPTION]
            : documentTemplate.states,
        is_all_states: documentTemplate.states && documentTemplate.states.length === statesWithoutEmpty.length,
      }}
      validationSchema={getDocumentTemplateValidationScheme(
        documentTypesDict,
        forbiddenNames,
        subOrganizationEnabled,
        multiCountryOrganization,
        shouldDisplayStates,
        supportedClaimTypes,
        documentTemplate.document_type
      )}
      onSubmit={handleDocumentSubmit}
      enableReinitialize
    >
      {(formikProps) => {
        const { isSubmitting, resetForm } = formikProps;

        return (
          <CardDialog
            isDialog={true}
            open={isOpen}
            maxWidth="sm"
            title="Update Document Template content"
            onClose={() => handleCancel(resetForm)}
            preventClose={isSubmitting}
          >
            <DocumentTemplateFormInner
              {...formikProps}
              uploadPercent={uploadPercent}
              onCancel={() => handleCancel(resetForm)}
              documentTemplate={documentTemplate}
            />
          </CardDialog>
        );
      }}
    </Formik>
  );
}

UpdateDocumentTemplateFormik.propTypes = {
  documentTemplate: PropTypes.object.isRequired,
  orgId: PropTypes.number.isRequired,
  onDocumentUpdate: PropTypes.func.isRequired,
  documentTemplates: PropTypes.array.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const EXPORT_DOCUMENTS_MAIN_ID = 'document_template_ids';

export function ExportDocumentTemplates({ onCancel, documentTemplates }) {
  const { organizationId } = useOrganization();

  const handleDocumentExport = async (values, formikProps) => {
    const { setSubmitting } = formikProps;

    const params = {
      document_template_ids: values[EXPORT_DOCUMENTS_MAIN_ID],
    };

    try {
      const response = await axios.post(
        `/sysconfig/api/v1/document_templates/organizations/${organizationId}/export_document_templates`,
        params,
        {
          responseType: 'blob',
        }
      );
      downloadResponseAsFile(response, { fallbackFilename: 'document_templates.zip' });
      onCancel();
    } catch (error) {
      reportAxiosError(error);
    }
    setSubmitting(false);
  };

  const options = [
    {
      title: 'Document Templates',
      subtitle: 'Select document templates to export',
      entries: documentTemplates.map((template) => ({
        id: `${template.id}`,
        entryTitle: `${template.display_name} (${template.filename})`,
        secondaryAction: (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {serverDateTimeToLocal(template.last_updated_datetime)}
          </Text>
        ),
        isDisabled: false,
      })),
    },
  ];

  return (
    <Formik
      initialValues={{
        [EXPORT_DOCUMENTS_MAIN_ID]: [],
      }}
      onSubmit={handleDocumentExport}
      validationSchema={Yup.object().shape({
        [EXPORT_DOCUMENTS_MAIN_ID]: Yup.array().min(1, 'At least one document template must be selected'),
      })}
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <CardDialog
            containerClassName={styles.exportCardContainer}
            isDialog
            maxWidth="sm"
            title="Export Document Templates"
            onClose={onCancel}
            preventClose={isSubmitting}
            fullWidth
          >
            <SearchWithOptionsFormik
              mainFieldId={EXPORT_DOCUMENTS_MAIN_ID}
              options={options}
              isSelectAllEnabled
              label="Search Document"
              textMaxWidth="30ch"
            />
            <div>
              <div className={styles.exportCardDialogButtonsContainer}>
                <CancelButton withMarginRight onClick={onCancel} />
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  Export
                </Button>
              </div>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
}

ExportDocumentTemplates.propTypes = {
  onCancel: PropTypes.func.isRequired,
  documentTemplates: PropTypes.array.isRequired,
};

export function AddDocumentTemplate(props) {
  const { orgId, onDocumentUpload, onCancel, documentTemplates } = props;
  const [uploadPercent, setUploadPercent] = useState(0);
  const {
    organizationOperationalDetails: { documentTypesDict, subOrganizationEnabled },
  } = useSysconfig();
  const { organization } = useSysconfig();
  const { supportedClaimTypes } = useOrganization();
  const multiCountryOrganization = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.MULTI_COUNTRY_ORGANIZATION
  );

  const shouldDisplayStates = isOrganizationWithStates(organization) || multiCountryOrganization;

  const handleDocumentSubmit = async (values, formikProps) => {
    const { setSubmitting } = formikProps;

    const formData = getDocumentUploadFormData(values);
    const config = getDocumentUploadConfig(setUploadPercent);

    try {
      await axios.post(
        `/sysconfig/api/v1/document_templates/organizations/${orgId}/upload_document_template`,
        formData,
        config
      );
      await onDocumentUpload();
    } catch (error) {
      reportAxiosError(error);
    }
    setSubmitting(false);
  };

  const forbiddenNames = documentTemplates.map((doc) => doc.filename);

  return (
    <>
      <Formik
        initialValues={{
          file_size: '',
          filename: '',
          display_name: '',
          coverages: [],
          is_all_coverages: true,
          countries: multiCountryOrganization ? [] : [getOrganizationCountryCode(organization)],
          is_all_countries: false,
          states: [],
          comment: '',
          is_file_required: true,
          document_type: '',
          is_all_sub_orgs: false,
          sub_organization_ids: [],
          is_all_lobs: false,
          lobs: [],
        }}
        validationSchema={getDocumentTemplateValidationScheme(
          documentTypesDict,
          forbiddenNames,
          subOrganizationEnabled,
          multiCountryOrganization,
          shouldDisplayStates,
          supportedClaimTypes
        )}
        onSubmit={handleDocumentSubmit}
        enableReinitialize
      >
        {(formikProps) => {
          const { isSubmitting } = formikProps;

          return (
            <CardDialog
              isDialog={true}
              maxWidth="sm"
              title="Upload Document Template"
              onClose={onCancel}
              preventClose={isSubmitting}
            >
              <DocumentTemplateFormInner
                {...formikProps}
                uploadPercent={uploadPercent}
                onCancel={onCancel}
                uploadDocument
              />
            </CardDialog>
          );
        }}
      </Formik>
    </>
  );
}

AddDocumentTemplate.propTypes = {
  displayIcon: PropTypes.bool,
  orgId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDocumentUpload: PropTypes.func.isRequired,
  documentTemplates: PropTypes.array.isRequired,
};
